import React, { useState, useRef } from 'react';
import {
  Typography,
  Button,
  Box,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { InfoCard } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import { GithubSyncSection } from './GithubSyncSection';

const useStyles = makeStyles(theme => ({
  fileInput: {
    display: 'none',
  },
  fileName: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  statusMessage: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
  },
  success: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
}));

interface SettingsContentProps {
  api: DevxpApi;
}

export const SettingsContent = ({ api }: SettingsContentProps) => {
  const classes = useStyles();

  return (
    <Grid container spacing={3}>
      {/* GitHub Auto-Sync Section */}
      <Grid item xs={12}>
        <GithubSyncSection api={api} />
      </Grid>

      {/* Developer Names CSV Upload */}
      <Grid item xs={12} md={6}>
        <CsvUploadCard
          title="Upload Developer Names (CSV)"
          description="Upload a CSV file with developer usernames (one per line) to manually register identity mappings. The system hashes each name with SHA-256 and stores the masked-to-real mapping so analytics can display real names. Existing entries are updated on re-upload. Repository name mappings are synced automatically via the GitHub App above."
          inputId="devxp-dev-csv-upload"
          onUpload={csvContent => api.uploadCsv(csvContent)}
          classes={classes}
        />
      </Grid>

      {/* Repository Names CSV Upload */}
      <Grid item xs={12} md={6}>
        <CsvUploadCard
          title="Upload Repository Names (CSV)"
          description="Upload a CSV file with repository full names (one per line, e.g. myorg/myrepo) to manually register repository name mappings. The system hashes each name with SHA-256 and stores the masked-to-real mapping so the Repositories page can display real names. Existing entries are updated on re-upload."
          inputId="devxp-repo-csv-upload"
          onUpload={csvContent => api.uploadRepoCsv(csvContent)}
          classes={classes}
        />
      </Grid>
    </Grid>
  );
};

interface CsvUploadCardProps {
  title: string;
  description: string;
  inputId: string;
  onUpload: (csvContent: string) => Promise<{ message: string; count: number; error?: string }>;
  classes: Record<string, string>;
}

const CsvUploadCard = ({ title, description, inputId, onUpload, classes }: CsvUploadCardProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; isError: boolean } | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFileName(file.name);
    setStatusMessage(null);
    const reader = new FileReader();
    reader.onload = e => setFileContent(e.target?.result as string);
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!fileContent) return;
    setUploading(true);
    setStatusMessage(null);
    try {
      const result = await onUpload(fileContent);
      if (result.error) {
        setStatusMessage({ text: result.error, isError: true });
      } else {
        setStatusMessage({ text: result.message, isError: false });
        setSelectedFileName('');
        setFileContent('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (e: any) {
      setStatusMessage({ text: e.message || 'Upload failed', isError: true });
    } finally {
      setUploading(false);
    }
  };

  return (
    <InfoCard title={title}>
      <Typography variant="body2" color="textSecondary" paragraph>
        {description}
      </Typography>
      <Box display="flex" alignItems="center">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          className={classes.fileInput}
          onChange={handleFileSelect}
          id={inputId}
        />
        <label htmlFor={inputId}>
          <Button variant="outlined" component="span">
            Choose CSV File
          </Button>
        </label>
        {selectedFileName && (
          <Typography className={classes.fileName} variant="body2">
            {selectedFileName}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!fileContent || uploading}
          startIcon={<CloudUploadIcon />}
          style={{ marginLeft: 16 }}
        >
          {uploading ? 'Processing...' : 'Upload & Process'}
        </Button>
      </Box>
      {statusMessage && (
        <Box
          className={`${classes.statusMessage} ${statusMessage.isError ? classes.error : classes.success}`}
        >
          <Typography variant="body2">{statusMessage.text}</Typography>
        </Box>
      )}
    </InfoCard>
  );
};
